import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Stack } from '@mui/material';
import Skeleton from '@/components/@IntranetLibrary/Skeleton';
import { useAtomValue } from 'jotai';
import { MyInfoState } from '@/recoil/atom';
import { useDialog, useSnackbar } from '@/hooks/global-tools';
import { Body1 } from '@/components/@IntranetLibrary/Typography';
import SortIcon from '@/public/icons/approval/sort.svg';
import CommentItem, {
  CommentItemProps,
} from '@/components/Comment/CommentList/CommentItem';
import CommentInput, {
  type CommentInputRef,
} from '@/components/Comment/CommentInput';
import useApprovalComments from '@/hooks/approval/useApprovalComments';
import useApprovalCommentMutations, {
  sanitizeMentionIds,
  type UpdateApprovalCommentPayload,
} from '@/hooks/approval/useApprovalCommentMutations';
import {
  addApprovalReferrers,
  checkUnauthorizedPassports,
} from '@/hooks/approval/useApprovalMentionPermission';
import type { CommentMention } from '@/components/Comment/CommentInput/index.util';
import type { InternalParticipant } from '@/types/api';
import type { ApprovalViewReferrer, ApprovalViewSecurity } from './types';
import ApprovalLogRow from './ApprovalLogRow';
import MentionRefereeModal, {
  type MentionRefereeModalRef,
} from './MentionRefereeModal';
import {
  CommentHeader,
  CommentHeaderCount,
  CommentHeaderLeft,
  CommentList,
  CommentMoreButton,
  CommentRoot,
  CommentSortLabel,
} from './styles';
import {
  COMMENT_DRAFT_DEBOUNCE_MS,
  COMMENT_SORT_LS_KEY,
  commentDraftLsKey,
  type ApprovalCommentRow,
  type ApprovalLogItem,
  type CommentSortOrder,
} from './CommentSlot.types';

/**
 * 코멘트 영역 — 포탈 공용 CommentInput/CommentItem 에 approval mutation override 주입.
 * 정렬·드래프트는 localStorage 보존, 로그 행은 ApprovalLogRow 로 분기.
 */

type Props = {
  /** 품의 단건 ID(문서번호) — 코멘트 스레드 조회·작성과 본인 비교의 기준 */
  documentId: string;
  /** 기존 참조자 — 멘션 권한체크 대상에서 제외하기 위함. */
  referrers?: ApprovalViewReferrer[];
  /** 추가 참조자 — 동일하게 권한체크 대상에서 제외. */
  additionalReferrers?: ApprovalViewReferrer[];
  /** 공개레벨. 9 = 비공개(상신자 본인) → 참조인 추가 불가. */
  security?: ApprovalViewSecurity | null;
  /** 보정된 코멘트 카운트 통지 — count 권위는 본 슬롯, 부모(헤더 아이콘)는 표시만. */
  onCommentCountChange?: (count: number) => void;
  /** close-guard 용 "작성/수정 중" 판정 getter — 마운트 동안 꽂아두고 부모가 닫기 직전 호출. */
  commentDirtyRef?: MutableRefObject<(() => boolean) | null>;
  /** refresh()를 호스트에 노출 — 결재 성공 시 코멘트 리스트 재검증용(commentDirtyRef 와 동일 패턴). */
  commentRefreshRef?: MutableRefObject<(() => Promise<void>) | null>;
  /** 미권한자 참조인 일괄 추가 직후 호출 — useApprovalView 는 튜플키라 호스트의 실제 mutate 를 받는다. */
  onReferrersChanged?: () => void;
};

const CommentSlot = forwardRef<HTMLDivElement, Props>(
  (
    {
      documentId,
      referrers,
      additionalReferrers,
      security,
      onCommentCountChange,
      commentDirtyRef,
      commentRefreshRef,
      onReferrersChanged,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const myInfo = useAtomValue(MyInfoState);
    const dialog = useDialog();
    const { showError } = useSnackbar();

    // ----- 정렬 — localStorage 복원 -----
    // SSR 안전: 첫 렌더는 default('desc'). mount 후 localStorage 값으로 동기화.
    const [defaultOrder, setDefaultOrder] = useState<CommentSortOrder>('desc');
    useEffect(() => {
      try {
        const v = window.localStorage.getItem(COMMENT_SORT_LS_KEY);
        if (v === 'asc' || v === 'desc') setDefaultOrder(v);
      } catch {
        // ignore
      }
    }, []);

    // ----- 데이터 layer (v2 — 문서번호 기준 스레드) -----
    const {
      rows,
      commentCount,
      orderType,
      setOrderType,
      hasMore,
      loadMore,
      isLoading,
      refresh,
      applyThread,
    } = useApprovalComments(documentId, myInfo?.passportId ?? '', defaultOrder);

    // 헤더(TitleMetaBar) 코멘트 아이콘 카운트 동기화 — count 변동(작성/삭제/더보기) 시마다 부모로 통지.
    useEffect(() => {
      onCommentCountChange?.(commentCount);
    }, [commentCount, onCommentCountChange]);

    // ----- mutation overrides -----
    const {
      createApprovalComment,
      updateApprovalComment,
      deleteApprovalComment,
      likeApprovalComment,
    } = useApprovalCommentMutations();

    // 작성만 documentId 를 path 로 받고, 201 응답이 갱신된 스레드 전체 —
    // 그 응답으로 캐시를 교체하므로 등록 후 재조회 호출이 없다.
    const handleCreateComment = useCallback(
      async (payload: {
        content: string;
        mentions: string[];
        attachments: { name: string; link: string }[];
      }) => {
        const thread = await createApprovalComment(documentId, payload);
        await applyThread(thread);
      },
      [documentId, createApprovalComment, applyThread],
    );

    const handleUpdateComment = useCallback(
      (payload: UpdateApprovalCommentPayload) =>
        updateApprovalComment(documentId, payload),
      [documentId, updateApprovalComment],
    );

    const handleDeleteComment = useCallback(
      (commentId: string) => deleteApprovalComment(documentId, commentId),
      [documentId, deleteApprovalComment],
    );

    const handleLikeComment = useCallback(
      (commentId: string, isLike: boolean) =>
        likeApprovalComment(documentId, commentId, isLike),
      [documentId, likeApprovalComment],
    );

    // 정렬 토글 — 라벨 클릭 시 desc↔asc 즉시 전환 (팝업 없음, 레거시 전자결재 동작).
    const toggleSort = useCallback(() => {
      const next: CommentSortOrder = orderType === 'desc' ? 'asc' : 'desc';
      setOrderType(next);
      try {
        window.localStorage.setItem(COMMENT_SORT_LS_KEY, next);
      } catch {
        // ignore
      }
    }, [orderType, setOrderType]);

    // ----- 드래프트 autosave -----
    // 진입 시 localStorage 에서 1회 복원.
    const draftLsKey = useMemo(
      () => commentDraftLsKey(documentId),
      [documentId],
    );
    const [initialDraft, setInitialDraft] = useState<string>('');
    // 복원해 넣은 원본 — 탭 닫기 가드가 "이번 세션 편집분"만 가려내는 기준.
    const restoredDraftRef = useRef<string>('');
    // 3초 debounce — onContentChange 가 호출될 때마다 timeout 재시작.
    const draftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // debounce 가 아직 LS 에 못 쓴 최신 드래프트 — unmount 시 flush 해 3초 내 닫아도 보존.
    const pendingDraftRef = useRef<string>('');
    // 복원분과 달라진 시점부터 true. 에디터가 mount 때 복원본을 그대로 되돌려주는
    // onChange 는 사용자 편집이 아니므로 여기서 걸러진다.
    const draftTouchedRef = useRef(false);
    useEffect(() => {
      let stored: string | null = null;
      try {
        stored = window.localStorage.getItem(draftLsKey);
      } catch {
        // ignore
      }
      restoredDraftRef.current = stored ?? '';
      // 모달을 유지한 채 문서만 갈아끼우는 경로(다음 결재 문서 등)에서는 이 컴포넌트가
      // 언마운트되지 않아 직전 문서의 편집 흔적이 남는다. 키가 바뀌면 flush cleanup 이
      // 이전 키로 먼저 돌고 이 본문이 뒤에 오므로, 여기서 비워야 새 문서로 안 넘어간다.
      draftTouchedRef.current = false;
      pendingDraftRef.current = '';
      if (stored) setInitialDraft(stored);
      // documentId 변경 시 다시 복원
    }, [draftLsKey]);

    const handleDraftChange = useCallback(
      (html: string) => {
        // 복원본과 달라지는 순간이 "사용자 편집" — 아래 빈 값 분기(이미지만 남은 코멘트 포함)
        // 보다 먼저 봐야 그 경로에서도 탭 닫기 경고가 산다.
        if (html !== restoredDraftRef.current) draftTouchedRef.current = true;
        // 사실상 빈 HTML 이면 즉시 제거(debounce 우회) — 지우고 바로 닫아도 stale draft 가 안 남도록.
        const trimmed = html.replace(/<[^>]+>/g, '').trim();
        if (!trimmed) {
          if (draftTimerRef.current) {
            clearTimeout(draftTimerRef.current);
            draftTimerRef.current = null;
          }
          try {
            window.localStorage.removeItem(draftLsKey);
          } catch {
            // ignore
          }
          // initialDraft 도 비워 재진입 시 stale 복원 방지 (등록 케이스는 handleSubmitted 처리).
          pendingDraftRef.current = '';
          restoredDraftRef.current = '';
          setInitialDraft('');
          return;
        }
        // 비어있지 않으면 기존처럼 debounced save. 최신 값은 ref 에도 보관(unmount flush 용).
        pendingDraftRef.current = html;
        if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
        draftTimerRef.current = setTimeout(() => {
          try {
            window.localStorage.setItem(draftLsKey, html);
          } catch {
            // ignore
          }
        }, COMMENT_DRAFT_DEBOUNCE_MS);
      },
      [draftLsKey],
    );

    // unmount 시 timer 정리 + 못 쓴 드래프트 flush — debounce 전에 닫아도 보존. 빈 값은 flush 안 함.
    useEffect(() => {
      return () => {
        if (draftTimerRef.current) {
          clearTimeout(draftTimerRef.current);
          draftTimerRef.current = null;
        }
        if (pendingDraftRef.current) {
          try {
            window.localStorage.setItem(draftLsKey, pendingDraftRef.current);
          } catch {
            // ignore
          }
        }
      };
    }, [draftLsKey]);

    // CommentItem 의 `onChanged` 콜백 — 좋아요/수정/삭제 후 리스트 refetch.
    const refreshRef = useRef(refresh);
    refreshRef.current = refresh;
    const handleItemChanged = useCallback(() => {
      void refreshRef.current?.();
    }, []);

    useEffect(() => {
      if (!commentRefreshRef) return undefined;
      commentRefreshRef.current = refresh;
      return () => {
        commentRefreshRef.current = null;
      };
    }, [commentRefreshRef, refresh]);

    // CommentInput 의 `updateCommentList` — 작성 성공 후 호출됨. 드래프트도 비움.
    const handleSubmitted = useCallback(async () => {
      try {
        window.localStorage.removeItem(draftLsKey);
      } catch {
        // ignore
      }
      if (draftTimerRef.current) {
        clearTimeout(draftTimerRef.current);
        draftTimerRef.current = null;
      }
      // pending flush 대상도 비워 unmount flush 가 등록된 내용을 되살리지 않도록.
      pendingDraftRef.current = '';
      restoredDraftRef.current = '';
      draftTouchedRef.current = false;
      // initialDraft 도 비워 keepMounted 모달에서 stale 드래프트 재주입 방지.
      setInitialDraft('');
      // 리스트 갱신은 작성 201 응답(handleCreateComment)이 이미 처리 — 재조회 없음.
    }, [draftLsKey]);

    // ----- 작성 중 코멘트 close-guard -----
    // 작성(create) 입력창 ref + 인라인 수정 중인 코멘트 id 집합으로 "작성 중" 여부 집계.
    const createInputRef = useRef<CommentInputRef>(null);
    const editingIdsRef = useRef<Set<string>>(new Set());

    const handleEditModeChange = useCallback(
      (commentId: string, isEditing: boolean) => {
        if (isEditing) editingIdsRef.current.add(commentId);
        else editingIdsRef.current.delete(commentId);
      },
      [],
    );

    // 작성 입력창에 내용/이미지 업로드가 있거나, 인라인 수정창이 하나라도 떠 있으면 작성 중.
    const isDirty = useCallback(
      () =>
        (createInputRef.current?.hasContent?.() ?? false) ||
        editingIdsRef.current.size > 0,
      [],
    );

    // 부모(ApprovalModal)에 작성중 판정 getter 노출 — 마운트 동안만. unmount 시 정리.
    useEffect(() => {
      if (!commentDirtyRef) return;
      commentDirtyRef.current = isDirty;
      return () => {
        commentDirtyRef.current = null;
      };
    }, [commentDirtyRef, isDirty]);

    // 탭/창 닫기로 실제 유실되는 것만 판정. 인라인 수정본은 저장소에 남지 않아 항상 대상이고,
    // 작성 입력창은 이번 세션에서 바뀐 경우만 — localStorage 에서 복원해 넣은 드래프트는
    // 닫아도 그대로 남아 재진입 때 복원된다. (모달 X 닫기용 isDirty 는 안내가 목적이라 별개)
    const hasUnloadRisk = useCallback(
      () =>
        editingIdsRef.current.size > 0 ||
        (draftTouchedRef.current &&
          (createInputRef.current?.hasContent?.() ?? false)),
      [],
    );

    // 탭/창 닫기 가드. SPA 한계로 커스텀 모달 불가, 포탈 beforeunload 패턴 차용.
    useEffect(() => {
      const handleBeforeUnload = (event: BeforeUnloadEvent) => {
        if (!hasUnloadRisk()) return;
        event.preventDefault();
        event.returnValue = '작성중인 코멘트가 있습니다.\n화면을 닫으시겠어요?';
      };
      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }, [hasUnloadRisk]);

    // ----- 멘션 권한체크 → 미권한자 자동 참조인 -----
    // 미권한자 "참조인 추가 후 등록" 확인 모달 (Promise 게이트).
    const refereeModalRef = useRef<MentionRefereeModalRef>(null);

    // 이미 참조자(또는 추가 참조자)인 passportId 는 권한체크/등록 대상에서 제외.
    const existingRefereeIds = useMemo(
      () =>
        new Set(
          [...(referrers ?? []), ...(additionalReferrers ?? [])].map(
            (r) => r.passportId,
          ),
        ),
      [referrers, additionalReferrers],
    );

    const handleBeforeSubmit = useCallback(
      async (
        mentionedIds: string[],
        mentions?: CommentMention[],
      ): Promise<boolean> => {
        // 1) 멘션 없음 → 즉시 통과 (권한체크 호출 안 함).
        // 형식이 어긋난 식별자는 등록 시 서버가 걸러내므로 권한체크/참조인 대상에서도 제외.
        const validIds = sanitizeMentionIds(mentionedIds);
        if (validIds.length === 0) return true;

        // 2) 이미 참조자인 멘션 제외.
        const targetIds = validIds.filter((id) => !existingRefereeIds.has(id));
        if (targetIds.length === 0) return true;

        // 3) 권한체크 — 미권한자 집합 (fail-open: 실패 시 빈 Set).
        const unauthorized = await checkUnauthorizedPassports(
          documentId,
          targetIds,
        );
        if (unauthorized.size === 0) return true;

        // 4) 미권한자 멘션을 멘션 출현순(mentionedIds index)으로 정렬 — 응답순/파싱순 비의존.
        const unauthorizedMentions = (
          mentions?.filter((m) => unauthorized.has(m.id)) ?? []
        ).sort((a, b) => validIds.indexOf(a.id) - validIds.indexOf(b.id));

        // 5) 비공개(security=9)는 참조인 추가 불가 — 미권한자 존재 시 멘션 객체 유무와 무관하게 차단.
        if (security === 9) {
          return await new Promise<boolean>((resolve) => {
            let settled = false;
            const done = (v: boolean) => {
              if (settled) return;
              settled = true;
              resolve(v);
            };
            dialog.show({
              type: 'alert',
              message: t('비공개 문서는 참조인 추가가 불가능합니다.'),
              confirmButtonText: t('확인'),
              onConfirm: () => done(false),
              onAfterClose: () => done(false),
            });
          });
        }

        // 미권한자 멘션 객체를 못 찾은 경우(노드 data 부재 등) → 추가할 대상이 없으므로 통과.
        if (unauthorizedMentions.length === 0) return true;

        // 6) 일반 문서 → 미권한자 리스트 모달 (Modal + PassportItem, Promise 게이트).
        const refereeCandidates: InternalParticipant[] =
          unauthorizedMentions.map((m) => ({
            passportId: m.id,
            name: m.name ?? '',
            positionName: m.positionName ?? '',
            departmentName: m.departmentName ?? '',
            divisionName: m.divisionName ?? '',
            profileImageUrl: m.profileImageUrl ?? '',
          }));
        const confirmed =
          (await refereeModalRef.current?.open(refereeCandidates)) ?? false;

        if (!confirmed) return false;

        // 7) 미권한자를 참조인으로 일괄 추가 (v2 — passportId 배열 한 번에).
        try {
          await addApprovalReferrers(
            documentId,
            unauthorizedMentions.map((m) => m.id),
          );
          onReferrersChanged?.();
          return true;
        } catch {
          showError({
            message: t('참조인 추가에 실패했습니다.'),
          });
          return false;
        }
      },
      [
        documentId,
        existingRefereeIds,
        security,
        dialog,
        t,
        showError,
        onReferrersChanged,
      ],
    );

    return (
      <CommentRoot ref={ref}>
        <CommentHeader>
          <CommentHeaderLeft>
            {isLoading && rows.length === 0 ? (
              <Skeleton variant="text" width={80} height={20} />
            ) : (
              <>
                {t('코멘트')}{' '}
                <CommentHeaderCount>{commentCount}</CommentHeaderCount>
              </>
            )}
          </CommentHeaderLeft>
          <CommentSortLabel type="button" onClick={toggleSort}>
            <SortIcon width={12} height={12} />
            {orderType === 'desc' ? t('최신순') : t('오래된순')}
          </CommentSortLabel>
        </CommentHeader>

        {/* 포탈 공용 CommentInput — 빈 코멘트 차단/멘션 추출/토스트는 portal 내장, 드래프트만 외부 wiring */}
        <CommentInput
          ref={createInputRef}
          // v2 는 문서번호로 작성하므로 그룹 ID 를 보관하지 않음 — override 경로에선 미사용
          commentGroupId=""
          updateCommentList={handleSubmitted}
          folding={false}
          defaultFocused={false}
          inputStyle="top"
          service="board"
          createCommentOverride={handleCreateComment}
          updateCommentOverride={handleUpdateComment}
          initialContent={initialDraft}
          onContentChange={handleDraftChange}
          beforeSubmit={handleBeforeSubmit}
          imageUploadMode="inline"
          isApproval
        />

        <CommentList aria-label={t('코멘트 목록')}>
          <Stack direction="column" gap="24px">
            {isLoading && rows.length === 0
              ? [0, 1, 2].map((i) => (
                  <Stack key={`sk-${i}`} direction="row" gap="12px" aria-hidden>
                    <Skeleton variant="circle" width={36} height={36} />
                    <Stack direction="column" gap="6px" sx={{ flex: 1 }}>
                      <Skeleton variant="text" width={120} height={16} />
                      <Skeleton variant="text" width="100%" height={14} />
                      <Skeleton variant="text" width="70%" height={14} />
                    </Stack>
                  </Stack>
                ))
              : rows.map((row: ApprovalCommentRow) =>
                  row.type === 'log' ? (
                    <ApprovalLogRow
                      key={row.id}
                      item={row as ApprovalLogItem}
                    />
                  ) : (
                    <CommentItem
                      key={row.id}
                      item={row as unknown as CommentItemProps['item']}
                      isMobile={false}
                      inputStyle="top"
                      service="board"
                      onChanged={handleItemChanged}
                      deleteCommentOverride={handleDeleteComment}
                      likeCommentOverride={handleLikeComment}
                      createCommentOverride={handleCreateComment}
                      updateCommentOverride={handleUpdateComment}
                      imageUploadMode="inline"
                      isApproval
                      hideAttachmentList
                      onEditModeChange={handleEditModeChange}
                    />
                  ),
                )}
            {hasMore && (
              <CommentMoreButton disableRipple onClick={loadMore}>
                <Body1
                  sx={{ color: 'var(--dk-accent, #06C)', fontWeight: 400 }}
                >
                  {t('코멘트 더보기')}
                </Body1>
              </CommentMoreButton>
            )}
          </Stack>
        </CommentList>

        {/* 미권한자 "참조인 추가 후 등록" 확인 모달 — beforeSubmit 에서 open(Promise) */}
        <MentionRefereeModal ref={refereeModalRef} />
      </CommentRoot>
    );
  },
);
CommentSlot.displayName = 'CommentSlot';

export default CommentSlot;
